import { copyText } from "@/lib/uid.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Plug, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { oauthApi } from "@/api/oauth.ts";
import { Alert, Button, Card } from "@/ui/primitives.tsx";

/** Outside apps connected over MCP + the server's MCP URL (the original's "Info (MCP URL)"). */
export function ConnectionsSettings() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const info = useQuery({ queryKey: ["oauth", "info"], queryFn: oauthApi.info });
  const grants = useQuery({ queryKey: ["oauth", "grants"], queryFn: oauthApi.grants });
  const revoke = useMutation({ mutationFn: oauthApi.revoke, onSuccess: () => void qc.invalidateQueries({ queryKey: ["oauth", "grants"] }) });
  const [copied, setCopied] = useState(false);
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <h3 className="flex items-center gap-2 font-semibold"><Plug className="size-4 text-fg-muted" /> {t("connections.mcpTitle")}</h3>
        <p className="text-sm text-fg-muted">{t("connections.mcpHint")}</p>
        {info.data && (
          <div className="flex items-center gap-2">
            <code dir="ltr" className="flex-1 truncate rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-sm">{info.data.mcpUrl}</code>
            <Button variant="secondary" size="sm" onClick={() => { void copyText(info.data!.mcpUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />} {t("common.copy")}</Button>
          </div>
        )}
        <ol className="list-decimal space-y-1 ps-5 text-xs text-fg-muted">
          <li>{t("connections.step1")}</li>
          <li>{t("connections.step2")}</li>
          <li>{t("connections.step3")}</li>
        </ol>
        {info.data && !info.data.issuer.startsWith("https://") && <Alert tone="warn">{t("connections.httpsWarn")}</Alert>}
      </Card>
      <Card className="space-y-2">
        <h3 className="font-semibold">{t("connections.apps")}</h3>
        {grants.data?.length === 0 && <p className="text-sm text-fg-muted">{t("connections.none")}</p>}
        <ul className="divide-y divide-border">
          {(grants.data ?? []).map((g) => (
            <li key={g.clientId} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 text-sm">
              <div className="min-w-48 flex-1">
                <div className="font-medium">{g.clientName} {safeHttps(g.clientUri) && <a className="ms-1 text-xs text-fg-muted underline" href={g.clientUri!} target="_blank" rel="noreferrer" dir="ltr">{safeHttps(g.clientUri)}</a>}</div>
                <div className="text-[11px] text-fg-muted">{t("connections.granted")} {fmt(g.createdAt)}{g.lastUsedAt ? ` · ${t("connections.lastUsed")} ${fmt(g.lastUsedAt)}` : ""} · <span dir="ltr">{g.scope}</span></div>
              </div>
              <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables === g.clientId} onClick={() => revoke.mutate(g.clientId)}><Trash2 className="size-3.5" /> {t("connections.revoke")}</Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/** Host of an https URL, else null (client-supplied links are untrusted). */
function safeHttps(u: string | null | undefined): string | null {
  if (!u) return null;
  try { const p = new URL(u); return p.protocol === "https:" ? p.host : null; } catch { return null; }
}
